import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { OnboardingIdStorage } from '@app/utils/services/onboarding-id-storage.service';
import { ConfigService } from '@app/core/config/config.service';
import {
  ContactInfo,
  SatInfo,
  LegalRepresentative,
  LaborInfo,
  FinancialProfile,
  CompanyInfo,
  RetrieveImagesRequest,
  DocumentUrlRequest,
  ConfirmUploadRequest
} from '../interfaces/kyc';
// Interfaces compartidas con KYB — los payloads son idénticos por contrato
// backend (mismo endpoint independientemente del flow). Reusamos las
// definiciones para evitar duplicación.
import {
  KybBusinessContact,
  KybBeneficialOwner,
  KybResourceProvidersToggle,
  KybResourceProvidersBulkRequest,
  KybResourceProvidersBulkResponse,
  KybPaymentBeneficiariesBulkRequest,
  KybSolicitantForm,
  KybApiResponse,
} from '@app/onboarding/kyb/interfaces/kyb';

@Injectable({
  providedIn: 'root'
})
export class KycService {

  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private idStorage: OnboardingIdStorage,
    private configService: ConfigService
  ) {
    this.baseUrl = `${this.configService.baseUrl}onboarding`;
  }

  startOnboarding(personType: string, product: string): Observable<any> {
    // Modo mock (revisión de maquetas): el caller guarda response.id en sessionStorage
    // para que los siguientes pasos pasen el check de getId(). MOCK_ID es un sentinel.
    if (this.configService.isMockBackend) return of({ id: 'MOCK_ID' });
    const params = new URLSearchParams({ personType, product });
    const url = `${this.baseUrl}/start?${params.toString()}`;
    return this.http.post(url, {});
  }

  showSteps(): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/steps`;
    return this.http.get(url);
  }

  saveContactInfo(data: ContactInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/contact-info`;
    return this.http.post(url, data);
  }

  saveSatInfo(data: SatInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/sat-info`;
    return this.http.post(url, data);
  }

  saveLegalRepresentative(data: LegalRepresentative): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/legal-representative`;
    return this.http.post(url, data);
  }

  uploadDocuments(formData: FormData): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/documents/upload`;
    return this.http.post(url, formData);
  }

  getDocumentUploadUrl(data: DocumentUrlRequest): Observable<any> {
    if (this.configService.isMockBackend) {
      const fakeKey = `mock-${Date.now()}`;
      return of({ data: { url: `mock://${fakeKey}`, s3Key: fakeKey } });
    }
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión.');
    }
    const params = new URLSearchParams({
      documentType: data.documentType,
      fileName: data.fileName,
      contentType: data.contentType,
    });
    const url = `${this.baseUrl}/${idOnboarding}/documents/presigned-url?${params.toString()}`;
    return this.http.post(url, {});
  }

  // Este método NO pasa por el interceptor porque s3Url apunta a S3, no al baseUrl del API
  uploadToS3(s3Url: string, file: File, contentType: string): Observable<HttpEvent<unknown>> {
    // Modo mock: emitimos un Response event directo para cerrar el stream sin subir a S3.
    if (this.configService.isMockBackend) {
      return of(new HttpResponse({ status: 200, body: null })) as Observable<HttpEvent<unknown>>;
    }
    const headers = new HttpHeaders({
      'Content-Type': contentType
    });

    return this.http.put(s3Url, file, {
      headers: headers,
      reportProgress: true,
      observe: 'events'
    });
  }

  confirmDocumentUpload(data: { documentType: string, s3Key: string }): Observable<any> {
    // Modo mock: el caller valida que confirmRes.data.s3Key coincida con el enviado.
    if (this.configService.isMockBackend) return of({ data: { s3Key: data.s3Key, documentId: Date.now() } });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión.');
    }

    const params = new URLSearchParams({
      documentType: data.documentType,
      s3Key: data.s3Key,
    });
    const url = `${this.baseUrl}/${idOnboarding}/documents/confirm?${params.toString()}`;
    return this.http.post(url, {});
  }

  /** Devuelve una URL firmada de S3 (TTL corto) para visualizar el doc en otra
   *  pestaña. Backend: `GET /onboarding/{processId}/documents/{documentId}/download-url`
   *  → `{ data: { url } }`. */
  getDocumentDownloadUrl(documentId: number): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({ data: { url: `mock://download/${documentId}` } });
    }
    const url = `${this.baseUrl}/${this.getId()}/documents/${documentId}/download-url`;
    return this.http.get(url);
  }

  nextStep(data: any = {}): Observable<any> {
    // Modo mock (revisión de maquetas): no se invoca el backend.
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/next`;
    return this.http.post(url, data);
  }

  submitFinal(data: any = {}): Observable<any> {
    // Modo mock (revisión de maquetas): no se invoca el backend.
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/submit`;
    return this.http.post(url, { acceptedPrivacyPolicy: true });
  }

  registerShareholders(data: any): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/shareholders`;
    return this.http.post(url, data);
  }

  getCurrentStep(): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/current-step`;
    return this.http.get(url);
  }

  /** Rehidrata el estado completo del proceso (para load inicial o F5).
   *  En modo mock, el caller (KycFlowStateService) no invoca este metodo — corta antes. */
  getState(): Observable<any> {
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/state`;
    return this.http.get(url);
  }

  saveLaborInfo(data: LaborInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/labor-info`;
    return this.http.post(url, data);
  }

  saveFinancialProfile(data: FinancialProfile): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/financial-profile`;
    return this.http.post(url, data);
  }

  saveCompanyInfo(data: CompanyInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const idOnboarding = this.getId();
    if (idOnboarding === '') {
      throw new Error('No se encontró un ID de onboarding en la sesión. No se puede continuar.');
    }
    const url = `${this.baseUrl}/${idOnboarding}/company-info`;
    return this.http.post(url, data);
  }

  // ═════════════════════════════════════════════════════════════════════
  // Endpoints CARDS (spec mayo 2026 — guía sec.5 PFAE+CARDS / sec.8 PM+CARDS)
  // Mismo contrato que KYB; reusamos las interfaces de kyb/interfaces.
  // ═════════════════════════════════════════════════════════════════════

  /** Contacto del negocio (CARDS — pantalla 6). */
  saveBusinessContact(data: KybBusinessContact): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/business-contact`;
    return this.http.post(url, data);
  }

  /** Datos del propietario real / beneficiario final (CARDS — pantalla 6). */
  saveBeneficialOwner(data: KybBeneficialOwner): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/beneficial-owner`;
    return this.http.post(url, data);
  }

  /** Toggle de proveedor de recursos (CARDS — pantalla 6). True/false controla
   *  si la sección está habilitada. Si false, el back borra los proveedores
   *  y sus docs automáticamente. */
  toggleResourceProviders(data: KybResourceProvidersToggle): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/resource-providers/toggle`;
    return this.http.post(url, data);
  }

  /** Bulk de proveedores de recursos (CARDS — pantalla 6).
   *  Reemplaza atómicamente la colección. Cada proveedor incluye sus s3Key
   *  previamente confirmados. */
  bulkResourceProviders(data: KybResourceProvidersBulkRequest): Observable<KybApiResponse<KybResourceProvidersBulkResponse>> {
    if (this.configService.isMockBackend) {
      return of<KybApiResponse<KybResourceProvidersBulkResponse>>({
        success: true,
        code: 'OK',
        message: 'Mock providers bulk',
        data: {
          providers: data.providers.map((p, i) => ({
            id: 1000 + i,
            tempId: p.tempId,
            documentsLinked: p.documents.length,
          })),
        },
      });
    }
    const url = `${this.baseUrl}/${this.getId()}/resource-providers/bulk`;
    return this.http.post<KybApiResponse<KybResourceProvidersBulkResponse>>(url, data);
  }

  /** Bulk de beneficiarios de PAGO (CARDS — pantalla 6, sólo INE + nombre,
   *  sin tel/correo/%). Al menos 1 obligatorio per guía sec.5 y sec.8. */
  bulkPaymentBeneficiaries(data: KybPaymentBeneficiariesBulkRequest): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({
        data: {
          paymentBeneficiaries: data.paymentBeneficiaries.map((b, i) => ({
            id: 3000 + i,
            tempId: b.tempId,
            documentsLinked: b.documents.length,
          })),
        },
      });
    }
    const url = `${this.baseUrl}/${this.getId()}/payment-beneficiaries/bulk`;
    return this.http.post(url, data);
  }

  /** Formulario PLD/KYC del solicitante (CARDS — pantalla 7).
   *  PFAE+CARDS y PM+CARDS mandan el mismo subset corto (primaryIncomeSource,
   *  primaryIncomeRange, depositFrequency, resourcesOrigin). */
  saveSolicitantForm(data: KybSolicitantForm): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/solicitant-form`;
    return this.http.post(url, data);
  }

  getId(): string {
    return this.idStorage.get() ?? '';
  }
}